import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Book, Image, Quote, Target, Star, LogOut, ListOrdered, Presentation } from 'lucide-react';

interface CommandItem {
  title: string;
  description: string;
  command: (props: any) => void;
}

interface SlashCommandsListProps {
  items: CommandItem[];
  command: (item: CommandItem) => void;
}

interface SlashCommandsListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const commandIcons: Record<string, React.ElementType> = {
  'Scripture': Book,
  'Illustration': Image,
  'Quote': Quote,
  'Application': Target,
  'Main Point': Star,
  'Delete Section': LogOut,
  'Collection': ListOrdered,
  'Slide': Presentation,
  'Add to Slides': Presentation,
};

export const SlashCommandsList = forwardRef<SlashCommandsListRef, SlashCommandsListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }
      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }
      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden min-w-[300px] border border-gray-200 max-h-[300px] overflow-y-auto">
      {props.items.map((item, index) => {
        const Icon = commandIcons[item.title] || Book;
        return (
          <button
            key={index}
            className={`w-full text-left px-4 py-3 flex items-center gap-3 ${
              index === selectedIndex ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'
            } ${index > 0 ? 'border-t border-gray-100' : ''}`}
            onClick={() => selectItem(index)}
            type="button"
          >
            <Icon className={`h-5 w-5 ${index === selectedIndex ? 'text-indigo-600' : 'text-gray-500'}`} />
            <div>
              <div className="font-medium">{item.title}</div>
              <div className={`text-sm ${index === selectedIndex ? 'text-indigo-500' : 'text-gray-500'}`}>
                {item.description}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
});

SlashCommandsList.displayName = 'SlashCommandsList';